import { PillBadge } from "../Primitives";
import { Pencil, Trash2, Users } from "lucide-react";
import { toneForStatus } from "./shiftUtils";
import { fmtTime } from "../../lib/tz";

// iter321 — Compact guard label used inside shift cells so multiple
// assigned officers fit without wrapping. "John Michael Smith" →
// "SMITH J." Falls back to whatever the backend sent for single-token
// names or ids. Hover shows the full guard name via the parent title.
function _shortName(name) {
  if (!name) return "";
  const parts = String(name).trim().split(/\s+/).filter(Boolean);
  if (parts.length < 2) return name;
  const last = parts[parts.length - 1];
  const first = parts[0][0].toUpperCase();
  return `${last} ${first}.`;
}

export function ShiftCard({ shift, isConflict, isVacationConflict, shortRestMinutes, onEdit, onEditSeries, onDeleteSeries, onDelete, density = "roomy" }) {
  const isOpen = shift.is_open || (!shift.guard_ids?.length);
  const inactiveOnShift = (shift.inactive_guard_ids && shift.inactive_guard_ids.length > 0);
  const minGuards = typeof shift.min_guards === "number" ? shift.min_guards : 1;
  const assigned = shift.guard_ids ? shift.guard_ids.length : 0;
  const isUnderstaffed = shift.is_understaffed || (assigned > 0 && assigned < minGuards);
  // Officers who have marked themselves off (or have an approved vacation)
  // for this shift's calendar day — surfaced by the backend enrichment.
  const unavail = Array.isArray(shift.unavailable_guards) ? shift.unavailable_guards : [];
  const hasUnavail = unavail.length > 0;

  // Priority: inactive > conflict > vacation > time-off > open > understaffed > normal
  let cardCls = "border-[#27272A] bg-[#121212] hover:border-[#FF3B30]";
  let badgeTone = toneForStatus(shift.status);
  let badgeText = (shift.status || "scheduled").replace("_", " ");
  let title = "Click to edit";
  if (inactiveOnShift) {
    cardCls = "border-[#FF3B30] bg-[#FF3B30]/20 hover:bg-[#FF3B30]/30 ring-1 ring-[#FF3B30]/40";
    badgeTone = "red"; badgeText = "⚠ inactive guard";
    title = "Inactive  — click to reassign";
  } else if (isConflict) {
    cardCls = "border-[#FF3B30] bg-[#FF3B30]/10 hover:bg-[#FF3B30]/20";
    badgeTone = "red"; badgeText = "⚠ conflict";
    title = "⚠ Schedule conflict — click to resolve";
  } else if (isVacationConflict) {
    cardCls = "border-[#FFD60A] bg-[#FFD60A]/15 hover:bg-[#FFD60A]/25 ring-1 ring-[#FFD60A]/40";
    badgeTone = "amber"; badgeText = "⚠ on vacation";
    title = "Officer has an approved vacation covering this date — reassign or reject the vacation.";
  } else if (hasUnavail) {
    cardCls = "border-[#FFD60A] bg-[#FFD60A]/10 hover:bg-[#FFD60A]/20 ring-1 ring-[#FFD60A]/30";
    badgeTone = "amber"; badgeText = "⚠ requested off";
    title = "Assigned  has marked themselves unavailable for this day — reassign or coordinate coverage.";
  } else if (isOpen) {
    cardCls = "border-[#FF9F0A] bg-[#FF9F0A]/10 hover:bg-[#FF9F0A]/20";
    badgeTone = "amber"; badgeText = "open";
    title = "Open shift (unassigned) — click to assign";
  } else if (isUnderstaffed) {
    cardCls = "border-[#FF9F0A] bg-[#FF9F0A]/10 hover:bg-[#FF9F0A]/20";
    badgeTone = "amber"; badgeText = `understaffed ${assigned}/${minGuards}`;
    title = `Understaffed: ${assigned} of ${minGuards} guards. Click to assign more.`;
  }

  return (
    <div
      data-testid={`shift-cell-${shift.id}`}
      draggable
      onDragStart={(e) => e.dataTransfer.setData("shift-id", shift.id)}
      onClick={(e) => { e.stopPropagation(); onEdit(shift); }}
      className={`border p-1.5 group transition-colors cursor-pointer ${cardCls}`}
      title={title}
    >
      <div className="flex items-start justify-between gap-1">
        <PillBadge tone={badgeTone}>{badgeText}</PillBadge>
        <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100">
          {shift.recurrence_group && (
            <>
              <button
                data-testid={`edit-series-${shift.recurrence_group}`}
                onClick={(e) => { e.stopPropagation(); onEditSeries(shift); }}
                title="Edit entire series"
                className="text-[#0A84FF] hover:text-white"
              >
                <Pencil className="size-3" />
              </button>
              <button
                data-testid={`del-series-${shift.recurrence_group}`}
                onClick={(e) => onDeleteSeries(shift.recurrence_group, e)}
                title="Delete entire series"
                className="text-[#FF9F0A] hover:text-[#FF3B30]"
              >
                <span className="text-[10px] font-bold">×↻</span>
              </button>
            </>
          )}
          <button
            onClick={(e) => { e.stopPropagation(); onDelete(shift.id); }}
            title="Delete this shift"
            className="text-zinc-500 hover:text-[#FF3B30]"
          >
            <Trash2 className="size-3" />
          </button>
        </div>
      </div>
      <div className="font-mono text-[10px] mt-1 text-zinc-400">
        {fmtTime(shift.start_time, shift.site_timezone, { hour: "2-digit", minute: "2-digit", ...(shift.site_timezone ? { timeZoneName: "short" } : {}) })}–
        {fmtTime(shift.end_time, shift.site_timezone, { hour: "2-digit", minute: "2-digit" })}
      </div>
      <div className="text-xs font-medium mt-0.5 truncate flex items-center gap-1" data-testid={`guards-${shift.id}`}>
        {(shift.guard_names && shift.guard_names.length > 1) && (
          <span className="inline-flex items-center gap-0.5 text-[9px] text-[#0A84FF] border border-[#0A84FF]/40 px-1 leading-tight">
            <Users className="size-2.5" />{shift.guard_names.length}
          </span>
        )}
        <span
          className={`truncate ${isOpen ? "text-[#FF9F0A] uppercase tracking-widest text-[10px] font-bold" : ""}`}
          title={shift.guard_names && shift.guard_names.length ? shift.guard_names.join(", ") : undefined}
        >
          {isOpen
            ? "Unassigned"
            : (shift.guard_names && shift.guard_names.length
                ? shift.guard_names.map(_shortName).join(", ")
                : "—")}
        </span>
      </div>
      {typeof shortRestMinutes === "number" && (
        // iter325 — Short-rest / turnaround warning. The hook already
        // enforces the agency-configured threshold (iter327) so the
        // pill just needs to render whenever a value is present.
        <div className="mt-1" data-testid={`short-rest-${shift.id}`}>
          <span
            title={`Only ${Math.floor(shortRestMinutes / 60)}h ${shortRestMinutes % 60}m since this officer's previous shift ended — consider redistributing.`}
            className="inline-flex items-center gap-1 border border-[#FF3B30] bg-[#FF3B30]/15 px-1.5 py-0.5 text-[9px] uppercase tracking-widest text-[#FF3B30] font-bold"
          >
            <span>Short rest</span>
            <span className="font-mono">·{Math.floor(shortRestMinutes / 60)}h{String(shortRestMinutes % 60).padStart(2, "0")}</span>
          </span>
        </div>
      )}
      {hasUnavail && (
        <div
          className="mt-1 flex flex-wrap gap-1"
          data-testid={`shift-unavailability-${shift.id}`}
        >
          {unavail.map((u) => {
            const gname = (shift.guard_names || [])[(shift.guard_ids || []).indexOf(u.guard_id)] || u.guard_id;
            // iter320 — Show last name only on the pill (full name in
            // the hover tooltip). Guard names on the shift cell were
            // running out of horizontal room even in 1-week roomy view.
            const parts = String(gname).trim().split(/\s+/).filter(Boolean);
            const lastName = parts.length > 1 ? parts[parts.length - 1] : gname;
            const reasons = (u.reasons && u.reasons.length) ? u.reasons.join(", ") : null;
            const isVac = u.source === "vacation";
            const fullLabel = isVac ? "VACATION" : "OFF REQUESTED";
            const initials = parts.slice(0, 2).map((w) => w[0]?.toUpperCase()).join("") || gname.slice(0, 2).toUpperCase();
            const shortLabel = isVac ? "VAC" : "OFF";
            const tip = `${gname} — ${fullLabel}${reasons ? ` · ${reasons}` : ""}${u.notes ? ` · ${u.notes}` : ""}`;
            if (density === "compact") {
              return (
                <span
                  key={u.guard_id}
                  title={tip}
                  className="inline-flex items-center gap-0.5 border border-[#FFD60A]/60 bg-[#FFD60A]/10 px-1 py-0.5 text-[9px] uppercase tracking-wider text-[#FFD60A] font-bold"
                >
                  <span className="font-mono leading-none">{initials}</span>
                  <span className="leading-none">·{shortLabel}</span>
                </span>
              );
            }
            if (density === "medium") {
              return (
                <span
                  key={u.guard_id}
                  title={tip}
                  className="inline-flex items-center gap-1 border border-[#FFD60A]/60 bg-[#FFD60A]/10 px-1 py-0.5 text-[9px] uppercase tracking-widest text-[#FFD60A] font-bold max-w-full"
                >
                  <span className="truncate max-w-[60px]">{lastName}</span>
                  <span>· {shortLabel}</span>
                </span>
              );
            }
            // roomy (default 1-week view)
            return (
              <span
                key={u.guard_id}
                title={tip}
                className="inline-flex items-center gap-1 border border-[#FFD60A]/60 bg-[#FFD60A]/10 px-1.5 py-0.5 text-[9px] uppercase tracking-widest text-[#FFD60A] font-bold max-w-full"
              >
                <span className="truncate max-w-[80px]">{lastName}</span>
                <span>· {fullLabel}</span>
              </span>
            );
          })}
        </div>
      )}
      {shift.recurrence_group && (
        <div className="text-[9px] text-[#FF9F0A] uppercase tracking-widest mt-0.5">↻ series</div>
      )}
      {shift.series_label && (
        <div
          className="text-[10px] font-mono text-[#0A84FF] truncate mt-0.5"
          data-testid={`series-label-${shift.id}`}
          title={shift.series_label}
        >
          {shift.series_label}
        </div>
      )}
    </div>
  );
}
